import { useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'
import usePageBuilderStore from '../stores/pageBuilderStore'
import { useLanguageStore } from '../stores/languageStore'

export function usePageBuilderActions(pageId) {
  const queryClient = useQueryClient()
  const viewMode = usePageBuilderStore((state) => state.viewMode)
  const { currentLanguageCode: locale } = useLanguageStore()
  const draftStates = usePageBuilderStore((state) => state.draftStates)
  const localBlocks = usePageBuilderStore((state) => state.localBlocks)
  const setDraftStates = usePageBuilderStore((state) => state.setDraftStates)
  const clearDraftStates = usePageBuilderStore(
    (state) => state.clearDraftStates
  )
  const clearLocalBlocks = usePageBuilderStore(
    (state) => state.clearLocalBlocks
  )
  const setHasUnsavedChanges = usePageBuilderStore(
    (state) => state.setHasUnsavedChanges
  )

  const saveAllDrafts = useMutation({
    mutationFn: async () => {
      const savePromises = []

      // Save existing block drafts (only for blocks that exist in database)
      Object.entries(draftStates).forEach(([blockId, content]) => {
        // Skip local blocks (they start with 'local-')
        if (blockId.startsWith('local-')) {
          return
        }

        const payload = {
          revision_content: content || {},
          locale: locale,
        }
        savePromises.push(api.put(`/blocks/${blockId}`, payload))
      })

      // Create new local blocks
      localBlocks.forEach((block) => {
        // Merge field changes from draftStates into the block content
        const draftContent = draftStates[block.id] || {}
        const mergedContent = { ...block.content, ...draftContent }

        const payload = {
          page_id: Number(pageId),
          block_type_id: Number(block.block_type_id),
          name: block.name,
          content: mergedContent,
          settings: block.settings || {},
          sort_order: block.sort_order,
          is_active: true,
          locale: locale,
        }
        savePromises.push(api.post('/blocks', payload))
      })

      return Promise.all(savePromises)
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ['blocks', { page_id: pageId, view_mode: viewMode, locale }],
      })
      await queryClient.refetchQueries({
        queryKey: ['blocks', { page_id: pageId, locale }],
      })
      setDraftStates({})
      clearLocalBlocks()
      setHasUnsavedChanges(false)
    },
  })

  const publishAllDrafts = useMutation({
    mutationFn: async () => {
      await api.post('/blocks/publish-all-revisions', {
        page_id: pageId,
        locale: locale,
      })
      return api.patch(`/pages/${pageId}/publish`, {
        status: 'published',
      })
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ['blocks', { page_id: pageId, view_mode: viewMode, locale }],
      })
      await queryClient.refetchQueries({
        queryKey: ['blocks', { page_id: pageId, locale }],
      })
      await queryClient.refetchQueries({
        queryKey: ['page', pageId, locale],
      })
      await queryClient.refetchQueries({
        queryKey: ['pages'],
      })
      setDraftStates({})
      setHasUnsavedChanges(false)
    },
  })

  const handleSaveAll = useCallback(() => {
    if (Object.keys(draftStates).length === 0 && localBlocks.length === 0) {
      return
    }
    saveAllDrafts.mutate()
  }, [draftStates, localBlocks, saveAllDrafts])

  const handlePublishAll = useCallback(async () => {
    if (Object.keys(draftStates).length > 0) {
      try {
        await saveAllDrafts.mutateAsync()
        publishAllDrafts.mutate()
      } catch (error) {
        // Error handling
      }
    } else {
      publishAllDrafts.mutate()
    }
  }, [draftStates, saveAllDrafts, publishAllDrafts])

  const handleDiscardDraft = useCallback(async () => {
    // Clear local draft states
    setDraftStates({})
    setHasUnsavedChanges(false)

    // Get blocks to clear revisions
    const blocksRes = await api.get(`/blocks`, {
      params: {
        page_id: pageId,
        per_page: 100,
        view_mode: viewMode,
        locale: locale,
      },
    })
    const blocks = blocksRes.data.data || []

    // Clear existing revisions from database
    const blocksWithRevisions = blocks.filter((block) => {
      return (
        block.translations?.some((t) => t.revision_content) ||
        block.revision_content
      )
    })

    if (blocksWithRevisions.length > 0) {
      const clearPromises = blocksWithRevisions.map((block) => {
        const payload = {
          revision_content: null,
          has_revision: false,
          locale: locale,
        }
        return api.put(`/blocks/${block.id}`, payload)
      })

      try {
        await Promise.all(clearPromises)
        await new Promise((resolve) => setTimeout(resolve, 500))
        await queryClient.invalidateQueries({
          queryKey: ['blocks'],
        })
        await queryClient.refetchQueries({
          queryKey: [
            'blocks',
            { page_id: pageId, view_mode: viewMode, locale },
          ],
        })
      } catch (error) {
        // Error handling
      }
    } else {
      queryClient.invalidateQueries({
        queryKey: ['blocks', { page_id: pageId, view_mode: viewMode, locale }],
      })
    }
  }, [
    draftStates,
    queryClient,
    pageId,
    viewMode,
    locale,
    setHasUnsavedChanges,
    setDraftStates,
  ])

  return {
    handleSaveAll,
    handlePublishAll,
    handleDiscardDraft,
    isSaving: saveAllDrafts.isPending,
    isPublishing: publishAllDrafts.isPending,
  }
}
